import File from '../File';
import NewFile from '../NewFile';
import { NotificationModal } from 'views/components';
import PropTypes from 'prop-types';
import { fileActions } from 'core/File';
import isEmpty from 'lodash/isEmpty';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Content,
    FolderIconContainer,
    FolderLine,
    IconContainer,
    Title
} from './styled';
import {
    ExpandButton,
    Input,
    MaterialIcon
} from 'mol-ui';
import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useInput,
    useSelector,
    useToggle
} from 'hooks';

const Folder = ({
    data: {
        files = [],
        id,
        name,
        owner: {
            id: owner_id,
            first_name,
            last_name
        }
    }
}) => {

    const { dangerColor } = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        user: {
            id: user_id
        }
    } = useSelector('account');
    const {
        blocked,
        status
    } = useSelector('file');

    const [editing, setEditing] = useState(false);
    const [error, setError] = useState(false);
    const [newNameInput, newName, setNewName] = useInput('', {}, () => setError(false));
    const [opened, toggle] = useToggle(false);
    const [deleteModal, toggleDeleteModal, setDeleteModal] = useToggle(false);
    
    useEffect(() => {
        if (status === `folder-deleted-${ id }`) {
            setDeleteModal(false);
            dispatch(fileActions.setFileStatus(''));
        }
        if (status === `folder-updated-${ id }`) {
            setEditing(false);
            setNewName('');
            dispatch(fileActions.setFileStatus(''));
        }
    }, [status]);
    
    const cancelEdit = () => {
        setNewName('');
        setEditing(false);
    };

    const handleSetEditing = () => {
        setEditing(true);
        if (!newName) {
            setNewName(name);
        }
    };

    const handleUpdate = () => {
        if (validate()) {
            const data = {
                id,
                name: newName
            };
            dispatch(fileActions.updateFolder(data));
        }
    };

    const validate = () => {
        const newError = !newName;
        setError(newError);
        return !newError;
    };

    const ownFolder = user_id === owner_id;

    return (
        <>
            <Container>
                <FolderLine margin={ !!opened ? '0px 0px 10px 0px' : '0px 0px 20px 0px' }>
                    <FolderIconContainer onClick={ toggle }>
                        <MaterialIcon icon={ opened ? 'folder_open' : ownFolder ? 'folder' : 'folder_shared' } size={ 22 } />
                    </FolderIconContainer>
                    {
                        !editing
                            ? <>
                                {
                                    ownFolder &&
                                    <MaterialIcon icon="lock" size={ 20 } />
                                }
                                <Title>
                                    ({ files.length }) - { name }{ !ownFolder ? ` (${ last_name }, ${ first_name })` : '' }
                                </Title>
                                {
                                    !blocked &&
                                    <NewFile folder={{ id, name }} row={ false } />
                                }
                                {
                                    isEmpty(files) && ownFolder && !blocked &&
                                    <IconContainer onClick={ () => setDeleteModal(true) }>
                                        <MaterialIcon color={ dangerColor } icon="delete" size={ 20 } />
                                    </IconContainer>
                                }
                                {
                                    ownFolder && !blocked &&
                                    <IconContainer onClick={ handleSetEditing }>
                                        <MaterialIcon icon="edit" size={ 20 } />
                                    </IconContainer>
                                }
                            </>
                            : <>
                                <Input
                                    { ...newNameInput }
                                    disabled={ blocked }
                                    error={ error }
                                    fontSize="12px"
                                    height="15px"
                                    margin="0px 5px"
                                    width="200px"
                                />
                                <ExpandButton
                                    circle
                                    danger
                                    disabled={ blocked }
                                    icon="close"
                                    margin="0px 10px"
                                    size={ 20 }
                                    onClick={ cancelEdit }
                                />
                                <ExpandButton
                                    circle
                                    icon="check"
                                    loading={ blocked }
                                    size={ 20 }
                                    onClick={ handleUpdate }
                                />
                            </>
                    }
                </FolderLine>
                {
                    opened &&
                    <Content>
                        {
                            files.map(file =>
                                <File
                                    data={ file }
                                    folderId={ id }
                                    key={ file.id }
                                />
                            )
                        }
                        <NewFile
                            folder={{ id, name }}
                        />
                    </Content>
                }
            </Container>
            <NotificationModal
                active={ deleteModal }
                backCaption={ t('Cancelar') }
                caption={ t('Eliminar') }
                height="fit-content"
                loading={ status === 'folder-deleting' }
                title={ `${ t('¿Desea eliminar la carpeta') } "${ name }"?` }
                onBack={ toggleDeleteModal }
                onSubmit={ () => dispatch(fileActions.deleteFolder(id)) }
            />
        </>
    );
};

Folder.propTypes = {
    data: PropTypes.object
};

export default Folder;
